import {
    type FC,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getSyntaxHighlighter } from "../../../app/Common/Highlight/SyntaxHighlighter";
import DefaultError from "../../Common/Error/DefaultError";
import Error404 from "../../Common/Error/Error404";
import type { RootState } from "../../Reducers/DeveloperReducers";
import { useTitle } from "../../Utilities/TitleHook";
import DocumentationPageLoading from "./DocumentationPageLoading";
import DocumentationExamples, { type ExamplesType } from "./Examples/DocumentationExamples";

const DocumentationPage: FC<{ path: Array<string>; hash: string; }> = ({ path, hash }) => {
    const [ loading, setLoading ] = useState(true);
    const [ notFound, setNotFound ] = useState(false);
    const [ error, setError ] = useState(false);
    const [ page, setPage ] = useState("");
    const [ examples, setExamples ] = useState<ExamplesType>({});
    const abortController = useRef<AbortController>(null);
    const pageRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.user);

    useEffect(() => {
        if(!path.length) {
            setLoading(false);
            setNotFound(true);

            return;
        }

        abortController.current?.abort();
        abortController.current = new AbortController();
        const signal = abortController.current.signal;

        const builtPath = btoa(decodeURIComponent(path.join("/")));

        setLoading(true);
        setNotFound(false);
        setError(false);

        fetch(`/documentation?path=${builtPath}`, {
            headers: new Headers({
                "Accept": "application/json",
            }),
            signal,
        })
            .then(response => {
                if(response.status !== 200) {
                    if(response.status === 404) {
                        setNotFound(true);
                    }

                    throw new Error("Could not fetch documentation");
                }

                return response.json();
            })
            .then(result => {
                setPage(result.page);
                setExamples(result.examples);
                setLoading(false);
            })
            .catch(err => {
                if(err instanceof DOMException) {
                    // fetch was cancelled, ignore
                    return;
                }

                console.error("API Documentation: Error loading page", err);
                setLoading(false);
                setError(true);
            });
    }, [ path, hash ]);

    const title = useMemo(() => {
        return decodeURIComponent(path[path.length - 1]);
    }, [ path ]);

    useTitle(title);

    useEffect(() => {
        if(loading) {
            return;
        }

        if(!hash) {
            return;
        }

        const replaced = hash.replace("#", "");

        if(replaced) {
            document.querySelector(`#${replaced}`)?.scrollIntoView();
        }
    }, [ page, loading, hash ]);

    useEffect(() => {
        if(pageRef.current) {
            getSyntaxHighlighter()
                .then(highlighter => {
                    const elements = Array.from(pageRef.current?.querySelectorAll("pre > code") ?? []);
                    let textArea: undefined | HTMLTextAreaElement;

                    for(const element of elements) {
                        if(element.querySelector("pre.shiki")) {
                            // Already has syntax highlighting
                            continue;
                        }

                        let language;

                        for(let i = 0, l = element.classList.length; i < l; i++) {
                            if(element.classList[i].startsWith("language-")) {
                                language = element.classList[i].replace("language-", "");
                                break;
                            }
                        }

                        if(!language) {
                            return;
                        }

                        if(typeof textArea === "undefined") {
                            // We use a text area to decode characters like &lt; to < as there's no nice built-in way
                            // to do this in JavaScript.
                            // Changing the PHP code is also not useful as when we read it using `innerHTML` we end up
                            // with encoded strings again anyway
                            textArea = document.createElement("textarea");
                        }

                        textArea.innerHTML = element.innerHTML;
                        element.innerHTML = highlighter.codeToHtml(textArea.value, {
                            lang : language,
                            theme: "github-dark-dimmed",
                        });
                    }
                });
        }
    }, [ page ]);

    useEffect(() => {
        if(!page) {
            return;
        }

        const embedDiv = pageRef.current?.querySelector("#user-can-embed");

        if(!embedDiv) {
            return;
        }

        let content;
        const classes = [
            "p-4 shadow-md border-l-4",
        ];

        if(!user) {
            classes.push("border-yellow-400 bg-yellow-50 dark:border-yellow-500 dark:bg-yellow-500/10");
            content = "You're not currently logged in so we can't determine if you can use the Embedded API";
        } else if(user.can_embed) {
            classes.push("border-green-400 bg-green-50 dark:border-green-500 dark:bg-green-500/10");
            content = "Your account has access to the Embedded API!";
        } else {
            classes.push("border-blue-400 bg-blue-50 dark:border-blue-500 dark:bg-blue-500/10");
            content = "You don't currently have access to the Embedded API, please contact us at " +
                "developers@shopfront.com.au with details about what you'll be using the embedded API for";
        }

        const el = document.createElement("div");

        el.append(content);

        el.className = classes.join(" ");

        embedDiv.append(el);
    }, [ page, user ]);

    let content;

    if(loading) {
        content = (
            <DocumentationPageLoading />
        );
    } else if(error) {
        if(notFound) {
            content = <Error404 />;
        } else {
            content = <DefaultError />;
        }
    } else {
        const hasExamples = Object.keys(examples).length > 0;

        content = (
            <>
                <div
                    className={"documentation-content" + (hasExamples ? "" : " no-examples")}
                    dangerouslySetInnerHTML={{ __html: page }}
                    onClick={e => {
                        if(!(e.target instanceof Element)) {
                            return;
                        }

                        // Get the closest ancestor a tag
                        const link = e.target.closest("a");

                        if(link) {
                            const hrefAttr = link.getAttribute("href");

                            if(!hrefAttr) {
                                return;
                            }

                            const href = hrefAttr.trim();

                            if(!href.startsWith("#") && !href.startsWith("http")) {
                                e.preventDefault();
                                const oldPathname = window.location.pathname;

                                navigate(href);

                                // Move to the div
                                if(href.includes("#") && href.startsWith(oldPathname)) {
                                    const hash = href.split("#").pop();

                                    if(hash) {
                                        const node = document.querySelector(`#${hash}`);

                                        if(node) {
                                            node.scrollIntoView();
                                        }
                                    }
                                }
                            }
                        }
                    }}
                />
                <DocumentationExamples key={path.join("/")} examples={examples} />
            </>
        );
    }

    return (
        <div className="documentation-page" ref={pageRef}>
            {content}
        </div>
    );
};

export default DocumentationPage;
